/**
 * Recharts wrapper following the shadcn/ui chart pattern.
 *
 * Provides a `ChartContainer` that scopes a CSS-variable-based color
 * palette to all child Recharts components, plus typed `ChartTooltip`
 * and `ChartLegend` shells that delegate visuals to <ChartTooltipContent>
 * and <ChartLegendContent>.
 */
import * as React from 'react';
import * as RechartsPrimitive from 'recharts';
import { cn } from '../../lib/utils';

export interface ChartConfig {
  [k: string]: {
    label?: React.ReactNode;
    icon?: React.ComponentType;
    color?: string;
    theme?: { light: string; dark: string };
  };
}

interface ChartContextValue {
  config: ChartConfig;
}

const ChartContext = React.createContext<ChartContextValue | null>(null);

export function useChart(): ChartContextValue {
  const ctx = React.useContext(ChartContext);
  if (!ctx) throw new Error('useChart must be used within <ChartContainer>.');
  return ctx;
}

export const ChartContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    config: ChartConfig;
    children: React.ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>['children'];
  }
>(({ id, className, children, config, ...props }, ref) => {
  const uid = React.useId();
  const chartId = `chart-${(id ?? uid).replace(/:/g, '')}`;
  return (
    <ChartContext.Provider value={{ config }}>
      <div
        ref={ref}
        data-chart={chartId}
        className={cn(
          "flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-none [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-sector]:outline-none [&_.recharts-surface]:outline-none",
          className,
        )}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <RechartsPrimitive.ResponsiveContainer>{children}</RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
});
ChartContainer.displayName = 'ChartContainer';

/**
 * Strip every char that isn't safe inside a CSS attribute selector +
 * custom-property name. The chart id is normally hash-prefixed React
 * useId output, but defensive sanitisation here means an upstream
 * change that lets user input bleed in cannot turn this into CSS
 * injection.
 */
function sanitizeCssIdent(s: string): string {
  return s.replace(/[^A-Za-z0-9_-]/g, '_').slice(0, 64);
}

function ChartStyle({ id, config }: { id: string; config: ChartConfig }): React.ReactElement | null {
  const colorEntries = Object.entries(config).filter(([, v]) => v.theme || v.color);
  if (!colorEntries.length) return null;
  const safeId = sanitizeCssIdent(id);
  const css = colorEntries
    .map(([key, value]) => {
      const color = value.theme?.dark ?? value.color;
      if (!color) return null;
      const safeKey = sanitizeCssIdent(key);
      // Color is restricted to a tight pattern (hex, rgb()/hsl()-family
      // values, named colors). Anything more exotic is dropped.
      const safeColor = /^[#A-Za-z0-9_().,%/\s-]+$/.test(color) ? color : null;
      if (!safeColor) return null;
      return `  --color-${safeKey}: ${safeColor};`;
    })
    .filter(Boolean)
    .join('\n');
  return (
    <style
      dangerouslySetInnerHTML={{
        __html: `[data-chart="${safeId}"] {\n${css}\n}`,
      }}
    />
  );
}

export const ChartTooltip = RechartsPrimitive.Tooltip;

export const ChartTooltipContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    active?: boolean;
    payload?: Array<{
      name?: string;
      value?: number | string;
      dataKey?: string;
      color?: string;
      payload?: Record<string, unknown>;
    }>;
    label?: React.ReactNode;
    hideLabel?: boolean;
    indicator?: 'line' | 'dot';
  }
>(({ active, payload, label, className, hideLabel, indicator = 'dot' }, ref) => {
  const { config } = useChart();
  if (!active || !payload?.length) return null;
  return (
    <div
      ref={ref}
      className={cn(
        'grid min-w-[8rem] items-start gap-1.5 rounded-lg border border-border/50 bg-card px-2.5 py-1.5 text-xs shadow-xl',
        className,
      )}
    >
      {!hideLabel && label != null && (
        <div className="font-medium text-foreground">{label}</div>
      )}
      <div className="grid gap-1.5">
        {payload.map((item, idx) => {
          const key = item.name ?? item.dataKey ?? String(idx);
          const cfg = config[String(key)];
          const fillColor = item.color ?? cfg?.color;
          return (
            <div key={String(key) + idx} className="flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-muted-foreground">
              <span
                className={cn(
                  'shrink-0 rounded-[2px]',
                  indicator === 'line' ? 'h-0.5 w-3 self-center' : 'h-2.5 w-2.5 self-center',
                )}
                style={{ background: fillColor }}
              />
              <div className="flex flex-1 justify-between leading-none">
                <span className="text-muted-foreground">{cfg?.label ?? key}</span>
                <span className="font-mono font-medium tabular-nums text-foreground">
                  {typeof item.value === 'number' ? item.value.toLocaleString('de-DE') : item.value}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});
ChartTooltipContent.displayName = 'ChartTooltipContent';

export const ChartLegend = RechartsPrimitive.Legend;

export const ChartLegendContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    payload?: Array<{ value?: string; dataKey?: string; color?: string }>;
    verticalAlign?: 'top' | 'bottom';
  }
>(({ className, payload, verticalAlign = 'bottom' }, ref) => {
  const { config } = useChart();
  if (!payload?.length) return null;
  return (
    <div
      ref={ref}
      className={cn(
        'flex items-center justify-center gap-4',
        verticalAlign === 'top' ? 'pb-3' : 'pt-3',
        className,
      )}
    >
      {payload.map((item) => {
        const key = item.value ?? item.dataKey;
        const cfg = key ? config[String(key)] : undefined;
        return (
          <div key={String(key)} className="flex items-center gap-1.5">
            <span className="h-2 w-2 shrink-0 rounded-[2px]" style={{ background: item.color ?? cfg?.color }} />
            <span className="text-xs text-muted-foreground">{cfg?.label ?? key}</span>
          </div>
        );
      })}
    </div>
  );
});
ChartLegendContent.displayName = 'ChartLegendContent';
